import { useEffect, useRef } from 'react'
import { Check, X } from 'lucide-react'
import type { WireArticleBlock } from '../../../api/types'

interface Props {
  block: Extract<WireArticleBlock, { type: 'heading' }>
  onSave: (content: string) => void
  onCancel: () => void
}

export function HeadingBlockEditor({ block, onSave, onCancel }: Props) {
  const inputRef = useRef<HTMLInputElement>(null)
  const savedRef = useRef(false)

  useEffect(() => {
    inputRef.current?.focus()
    inputRef.current?.select()
  }, [])

  function commit() {
    if (savedRef.current) return
    savedRef.current = true
    const value = inputRef.current?.value.trim() ?? ''
    onSave(value || block.content)
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') { e.preventDefault(); commit() }
    if (e.key === 'Escape') { e.preventDefault(); onCancel() }
  }

  return (
    <div className="flex items-center gap-1.5 rounded border-2 border-accent-primary-solid bg-surface p-1.5">
      <input
        ref={inputRef}
        type="text"
        defaultValue={block.content}
        onKeyDown={handleKeyDown}
        onBlur={commit}
        className={`min-w-0 flex-1 bg-transparent outline-none ${
          block.level <= 2
            ? 'font-zodiak text-xl font-bold text-fg'
            : 'text-base font-bold text-fg'
        }`}
        aria-label="Edit heading text"
      />
      <button
        type="button"
        onMouseDown={(e) => { e.preventDefault(); commit() }}
        title="Save (Enter)"
        className="shrink-0 rounded p-1 text-fg-secondary hover:bg-fill hover:text-fg"
      >
        <Check size={13} />
      </button>
      <button
        type="button"
        onMouseDown={(e) => { e.preventDefault(); onCancel() }}
        title="Cancel (Esc)"
        className="shrink-0 rounded p-1 text-fg-secondary hover:bg-fill hover:text-fg"
      >
        <X size={13} />
      </button>
    </div>
  )
}
